import { keyframes } from '@emotion/react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { TMentionUser } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

interface MentionListProps {
  items: TMentionUser[];
  loading?: boolean;
  command: (attrs: { id: string; label: string }) => void;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

// Generate a consistent muted colour from a name string
const avatarColor = (name: string) => {
  const colors = ['#8EACCD', '#A8C5A0', '#C5A8A8', '#C5BCA8', '#A8A8C5', '#A8C5BC', '#C5A8BC'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    // oxlint-disable-next-line
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

export const MentionList = forwardRef<MentionListRef, MentionListProps>(({ items, loading, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) {
      command({ id: item.id, label: item.displayName });
    }
  };

  useEffect(() => setSelectedIndex(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex(i => (i + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex(i => (i + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!loading && items.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={6}
      sx={{
        minWidth: 240,
        maxWidth: 320,
        maxHeight: 280,
        overflow: 'auto',
        borderRadius: '16px',
        py: 0.75,
        // Remove any background/border injected by tippy light theme
        background: '#fff',
      }}
    >
      <List disablePadding>
        {loading ? (
          <ListItem disablePadding>
            <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[0, 1, 2].map(i => (
                <Box
                  key={i}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'grey.400',
                    animation: `${blink} 1.2s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                  }}
                />
              ))}
            </Box>
          </ListItem>
        ) : (
          items.map((item, index) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => selectItem(index)}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  mx: 0.75,
                  borderRadius: '10px',
                  backgroundColor: index === selectedIndex ? 'grey.100' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                <Avatar
                  src={getMediaUrl(item.avatar)}
                  alt={item.displayName}
                  sx={{
                    width: 34,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                    mr: 1.5,
                    bgcolor: avatarColor(item.displayName),
                    color: '#fff',
                  }}
                >
                  {item.displayName[0]}
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {item.displayName}
                  </Typography>
                  {item.username && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      (@{item.username})
                    </Typography>
                  )}
                </Box>
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  );
});

MentionList.displayName = 'MentionList';
